import React from 'react';
import styled from 'styled-components';

import { SiteDeliveryType } from 'data/__generated__';
import { Input } from '@/components/ui/input';
import { DataTableFacetedFilter } from 'components/DataTable/DataTableFacetedFilter';
import { DataTableBooleanFilter } from 'components/DataTable/DataTableBooleanFilter';
import { DataTableDateRangeFilter } from 'components/DataTable/DataTableDateRangeFilter';

export type DateRangeValue = { gte?: number; lte?: number };

export type DeliveryPlanningFilters = {
    name: string;
    launchDateRange?: DateRangeValue;
    removalDateRange?: DateRangeValue;
    isSecured?: boolean;
    hasSensei?: boolean;
    deliveryTypes: string[];
};

export const EMPTY_DELIVERY_PLANNING_FILTERS: DeliveryPlanningFilters = {
    name: '',
    deliveryTypes: [],
};

const DELIVERY_TYPE_OPTIONS = [
    { value: SiteDeliveryType.WarehouseDelivery, label: 'Entrepôt' },
    { value: SiteDeliveryType.MicrostoreDelivery, label: 'Microstore' },
    { value: SiteDeliveryType.PackageDelivery, label: 'Colis' },
    { value: SiteDeliveryType.RefrigeratedTruckDelivery, label: 'Camion frigorifique' },
];

type Props = {
    filters: DeliveryPlanningFilters;
    onChange: (next: DeliveryPlanningFilters) => void;
};

export const DeliveryPlanningFilterBar: React.FC<Props> = ({ filters, onChange }) => {
    const update = <K extends keyof DeliveryPlanningFilters>(key: K, value: DeliveryPlanningFilters[K]) =>
        onChange({ ...filters, [key]: value });

    return (
        <Bar>
            <Input
                placeholder="Filtrer par nom de site…"
                value={filters.name}
                onChange={(e) => update('name', e.target.value)}
                data-test="delivery-planning-name-filter"
                autoFocus
            />
            <FiltersRow>
                <DataTableFacetedFilter
                    title="Type de livraison"
                    multi
                    options={DELIVERY_TYPE_OPTIONS}
                    selectedValues={filters.deliveryTypes}
                    onChange={(values) => update('deliveryTypes', values)}
                />
                <DataTableBooleanFilter
                    title="Sécurisé"
                    value={filters.isSecured}
                    onChange={(value) => update('isSecured', value)}
                />
                <DataTableBooleanFilter
                    title="Sensei"
                    value={filters.hasSensei}
                    onChange={(value) => update('hasSensei', value)}
                />
                <DataTableDateRangeFilter
                    title="Date d'ouverture"
                    value={filters.launchDateRange}
                    onChange={(value) => update('launchDateRange', value)}
                />
                <DataTableDateRangeFilter
                    title="Date de fermeture"
                    value={filters.removalDateRange}
                    onChange={(value) => update('removalDateRange', value)}
                />
            </FiltersRow>
        </Bar>
    );
};

const Bar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 15px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    margin-bottom: 12px;
`;

const FiltersRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;
